import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Height to clear so an anchored section isn't hidden under the sticky nav.
const NAV_OFFSET = 88;

function jumpToHash(hash) {
  if (!hash) return false;
  const el = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!el) return false;
  const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET);
  // 'instant' (not 'smooth'): the destination page runs count-up/reveal rAF
  // loops on mount that interrupt a smooth scroll animation, leaving it at 0.
  // A direct jump is reliable and is the standard anchor-link behavior anyway.
  window.scrollTo({ top, left: 0, behavior: 'instant' });
  return true;
}

// Resets scroll on navigation. When the URL carries a #hash (e.g.
// /tools#bpm-finder from the nav/footer), scroll that section into view once
// the destination page has rendered, instead of landing at the top.
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // Retry across frames until the destination section exists, then re-assert
    // a couple of times: the page's motion engine runs a canScroll() probe on
    // mount that briefly bumps the scroll position, which can otherwise undo
    // the jump.
    let raf;
    let tries = 0;
    const go = () => {
      if (jumpToHash(hash) || tries++ > 40) return;
      raf = requestAnimationFrame(go);
    };
    raf = requestAnimationFrame(go);
    const t1 = setTimeout(() => jumpToHash(hash), 150);
    const t2 = setTimeout(() => jumpToHash(hash), 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname, hash]);

  // A <Link> to the current path with a different #hash is handled natively by
  // the browser (it never reaches the router), so catch those here too.
  useEffect(() => {
    const onHash = () => jumpToHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return null;
}

export default ScrollToTop;
